import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Lock,
  MoreHorizontal,
  Search,
  Settings,
  Share2,
  Trash2,
  UploadCloud,
  UserPlus,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppearanceMenu, type Appearance } from "@/components/projects/appearance-menu";
import { FolderCard } from "@/components/projects/folder-card";
import { FolderNameDialog } from "@/components/projects/folder-name-dialog";
import { FolderSettingsDialog } from "@/components/projects/folder-settings-dialog";
import { InviteToFolderDialog } from "@/components/projects/invite-to-folder-dialog";
import { ShareDialog, type ShareDialogItem } from "@/components/shares/share-dialog";
import { UploadMenu } from "@/components/projects/upload-menu";
import { ProjectShell, type Crumb } from "@/components/projects/project-shell";
import { VideoCard } from "@/components/projects/video-card";
import { useWindowedList } from "@/hooks/use-windowed-list";
import {
  VideoSortMenu,
  sortVideos,
  type VideoSortState,
} from "@/components/projects/video-sort-menu";
import {
  useCreateFolder,
  useDeleteFolder,
  useFolderContents,
  useUpdateFolder,
} from "@/hooks/use-folders";
import { useDeleteVideo, useDeleteVideoPermanent } from "@/hooks/use-videos";
import { useProject } from "@/hooks/use-projects";
import { SelectionActionBar } from "@/components/projects/selection-action-bar";
import type { Folder } from "@/lib/api-types";
import { useAuth } from "@/hooks/use-auth";
import { ApiError } from "@/lib/api";
import {
  canCreateShareLink,
  canDeleteFolder as canDeleteFolderRole,
  canManageFolderAccess,
} from "@/lib/api-types";
import { formatBytes } from "@/lib/utils";
import {
  isDesktopViewport,
  useSearchOverlay,
} from "@/components/layout/search-overlay-context";

type Props = {
  /** "/app/projects" or "/admin/projects" — for the breadcrumb link back to the project list. */
  projectsBasePath: string;
  /** "/app/upload" or "/admin/upload" — Upload page pre-fill target. */
  uploadPath: string;
  /** "/app/watch" or "/admin/watch" — VideoCard double-click builds `${watchPath}/${videoId}`. */
  watchPath: string;
};

const GRID_CLASSES = "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
const LIST_CLASSES = "flex flex-col gap-2";

/**
 * Folder detail page — same shell as ProjectDetailPage but scoped to one folder.
 *   - Upload menu creates subfolders in this folder + uploads assets to it.
 *   - Rename / Folder settings / Delete via the three-dot menu next to Share.
 *   - Restricted folder access: server 403 → blocked-entry screen (matches
 *     restricted-project pattern).
 */
export function FolderDetailPage({ projectsBasePath, uploadPath, watchPath }: Props) {
  const { projectId, folderId } = useParams<{ projectId: string; folderId: string }>();
  const navigate = useNavigate();
  const { role } = useAuth();

  const { data: project } = useProject(projectId);
  const {
    data: contents,
    isLoading,
    isError,
    error,
    refetch,
  } = useFolderContents(folderId);

  const createFolderMutation = useCreateFolder();
  const updateFolderMutation = useUpdateFolder();
  const deleteFolderMutation = useDeleteFolder();

  const [appearance, setAppearance] = useState<Appearance>("grid");
  const [sort, setSort] = useState<VideoSortState>({ field: "dateUploaded", dirIndex: 0 });
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Search overlay — desktop opens the shared overlay SCOPED to this folder.
  // Mobile keeps the in-page inline expand (in-place filter on the visible
  // rows) — same pattern as project-detail.
  const { openWith: openSearchOverlay } = useSearchOverlay();
  const handleSearchIconClick = () => {
    if (isDesktopViewport()) {
      if (!folderId) return;
      openSearchOverlay({
        kind: "folder",
        folderId,
        label: contents?.folder?.name,
      });
    } else {
      setSearchOpen(true);
    }
  };
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [renameOpen, setRenameOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const totalSelected = selectedIds.size + selectedFolders.size;

  // Folder-detail is scoped to a single project; passing projectId to the
  // video hooks so their query invalidation also refetches the project-level
  // videos list. Contents query is invalidated regardless.
  const deleteVideo = useDeleteVideo(projectId);
  const deleteVideoPermanent = useDeleteVideoPermanent(projectId);

  const foldersBasePath = `${projectsBasePath}/${projectId}/folders`;
  const uploadHref = `${uploadPath}?projectId=${projectId}&folderId=${folderId}`;

  // Invite gate mirrors the backend MANAGE_FOLDER_ACCESS (ADMIN + FULL_ACCESS).
  // Adding people to a public folder is a no-op today — it primes the ACL for
  // whenever the folder gets flipped to Restricted, matching how the project
  // members dialog behaves.
  const showInvite = canManageFolderAccess(role);
  const showShare = canCreateShareLink(role);
  const showSettings = canManageFolderAccess(role);
  const showDelete = canDeleteFolderRole(role);
  const gridClass = appearance === "grid" ? GRID_CLASSES : LIST_CLASSES;

  const subfolders = contents?.subfolders ?? [];
  const videos = contents?.videos ?? [];

  const visible = useMemo(() => {
    if (!videos.length) return [];
    const filtered = query.trim()
      ? videos.filter((v) => v.title?.toLowerCase().includes(query.trim().toLowerCase()))
      : videos;
    return sortVideos(filtered, sort);
  }, [videos, query, sort]);

  // Only mount a window of the (already sorted/filtered) grid; append on scroll.
  const { visible: pagedVideos, hasMore, sentinelRef } = useWindowedList(visible);

  const totalBytes = useMemo(
    () => videos.reduce((sum, v) => sum + (v.fileSizeBytes ?? 0), 0),
    [videos]
  );

  function toggleSelected(id: string) {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleFolderSelected(id: string) {
    setSelectedFolders((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * Card body single-click (2026-07-18) — replace the entire selection with
   * just this item. Wipes the other-type set too so switching a click between
   * videos and folders doesn't accumulate.
   */
  function selectVideoOnly(id: string) {
    setSelectedIds(new Set([id]));
    setSelectedFolders(new Set());
  }
  function selectFolderOnly(id: string) {
    setSelectedFolders(new Set([id]));
    setSelectedIds(new Set());
  }

  /** Selected items → ShareDialog payload. */
  const shareItems: ShareDialogItem[] = useMemo(() => {
    const videoItems: ShareDialogItem[] = videos
      .filter((v) => selectedIds.has(v.id))
      .map((v) => ({ type: "video" as const, id: v.id, name: v.title }));
    const folderItems: ShareDialogItem[] = subfolders
      .filter((f) => selectedFolders.has(f.id))
      .map((f) => ({ type: "folder" as const, id: f.id, name: f.name }));
    return [...folderItems, ...videoItems];
  }, [videos, subfolders, selectedIds, selectedFolders]);

  const selectedVideoObjects = useMemo(
    () => videos.filter((v) => selectedIds.has(v.id)),
    [videos, selectedIds],
  );
  // Subfolders come from GET /folders/:id/contents which returns a lean shape
  // (no itemCount etc.) — coerce to the Folder API type so the bar's callback
  // signature stays uniform with project-detail.
  const selectedFolderObjects: Folder[] = useMemo(
    () =>
      subfolders
        .filter((f) => selectedFolders.has(f.id))
        .map((f) => ({
          id: f.id,
          name: f.name,
          projectId: projectId ?? undefined,
          parentFolderId: folderId ?? null,
          restricted: !!f.restricted,
          canEnter: true,
          restrictedAncestorId: null,
        })),
    [subfolders, selectedFolders, projectId, folderId],
  );

  function clearSelection() {
    setSelectedIds(new Set());
    setSelectedFolders(new Set());
  }

  async function handleCreateFolder(name: string) {
    if (!projectId || !folderId) return;
    await createFolderMutation.mutateAsync({
      projectId,
      name,
      parentFolderId: folderId,
    });
  }

  async function handleRename(name: string) {
    if (!folderId || !contents) return;
    await updateFolderMutation.mutateAsync({
      folderId,
      name,
      projectId,
      parentFolderId: contents.folder.parentFolderId,
    });
  }

  function handleDelete() {
    if (!folderId || !contents) return;
    if (
      !confirm(
        `Move "${contents.folder.name}" and everything inside it to Trash?`
      )
    )
      return;
    deleteFolderMutation.mutate(
      {
        folderId,
        projectId,
        parentFolderId: contents.folder.parentFolderId,
      },
      {
        onSuccess: () => {
          const parent = contents.folder.parentFolderId;
          navigate(
            parent ? `${foldersBasePath}/${parent}` : `${projectsBasePath}/${projectId}`
          );
        },
      }
    );
  }

  // Blocked-entry screen — user hit /folders/:id directly but a restricted
  // ancestor blocks them. Matches the restricted-project blocked screen so
  // the pattern is consistent across the app.
  const status = (error as ApiError | undefined)?.status;
  if (isError && (status === 403 || status === 404)) {
    const restricted = status === 403;
    return (
      <ProjectShell
        projectId={projectId}
        projectsBasePath={projectsBasePath}
        crumbs={[
          { label: project?.name ?? "…", to: `${projectsBasePath}/${projectId}` },
          { label: restricted ? "Restricted" : "Not found" },
        ]}
      >
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-4 py-16 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-muted-foreground">
            <Lock className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div className="max-w-md">
            <h2 className="text-lg font-semibold text-foreground">
              {restricted ? "This folder is restricted" : "Folder not found"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {restricted
                ? "Ask a Workspace Admin or Full Access user to invite you before you can open it."
                : "It may have been deleted or moved."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(projectsBasePath)}>
              All Projects
            </Button>
            {projectId && (
              <Button onClick={() => navigate(`${projectsBasePath}/${projectId}`)}>
                Back to project
              </Button>
            )}
          </div>
        </div>
      </ProjectShell>
    );
  }

  if (isLoading || !contents) {
    return (
      <ProjectShell
        projectId={projectId}
        projectsBasePath={projectsBasePath}
        crumbs={[{ label: project?.name ?? "…" }]}
      >
        <div className="px-4 py-6 md:px-8 md:py-8">
          <div className="h-6 w-64 animate-pulse rounded bg-secondary/60" />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-video animate-pulse rounded-xl bg-secondary/60" />
            ))}
          </div>
        </div>
      </ProjectShell>
    );
  }

  const folder = contents.folder;
  const nothingAtAll = subfolders.length === 0 && videos.length === 0;

  // Breadcrumb for the shared top bar: All Projects / project / …ancestors / folder.
  const crumbs: Crumb[] = [
    { label: project?.name ?? "…", to: `${projectsBasePath}/${projectId}` },
    ...folder.breadcrumb.slice(0, -1).map((step) => ({
      label: step.name,
      to: `${foldersBasePath}/${step.id}`,
    })),
    { label: folder.name },
  ];

  return (
    <ProjectShell
      projectId={projectId}
      projectsBasePath={projectsBasePath}
      crumbs={crumbs}
      crumbTrailing={
        folder.restricted ? (
          <span className="ml-1 flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            <Lock className="h-3 w-3" strokeWidth={2} />
            Restricted
          </span>
        ) : undefined
      }
    >
      <div
        className="surface-panel flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-xl"
        onClick={(e) => {
          // Empty-space click (toolbar or grid) clears selection (2026-07-18 spec).
          if (totalSelected === 0) return;
          const el = e.target as HTMLElement | null;
          if (!el || el.closest(
            '[data-selectable-card], [data-selection-bar], button, a, input, textarea, select, [role="menuitem"], [role="menu"], [role="dialog"]'
          )) return;
          clearSelection();
        }}
      >
        {/* Toolbar — Appearance / Sort / Search + folder actions */}
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-4 py-2.5 text-sm md:px-6 md:gap-4">
          <AppearanceMenu value={appearance} onChange={setAppearance} />
          <VideoSortMenu value={sort} onChange={setSort} />
          {searchOpen ? (
            <div className="flex w-full max-w-xs items-center gap-1.5">
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search this folder…"
                className="h-8 text-sm"
              />
              <button
                type="button"
                aria-label="Close search"
                onClick={() => { setSearchOpen(false); setQuery(""); }}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              aria-label="Search this folder"
              onClick={handleSearchIconClick}
              className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <Search className="h-4 w-4" strokeWidth={1.75} />
            </button>
          )}

          <div className="flex-1" />

          <div className="flex shrink-0 items-center gap-2">
          <UploadMenu uploadHref={uploadHref} onCreateFolder={handleCreateFolder} />

          {showInvite && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Invite to this folder"
                  onClick={() => setInviteOpen(true)}
                  className="grid h-9 w-9 place-items-center rounded-full bg-secondary text-muted-foreground transition-colors hover:text-foreground"
                >
                  <UserPlus className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Invite to this folder</TooltipContent>
            </Tooltip>
          )}

          {showShare && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShareOpen(true)}
                    disabled={totalSelected === 0}
                    aria-label="Share"
                  >
                    <Share2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Share</span>
                    {totalSelected > 0 && (
                      <span className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 text-[10px] font-medium">
                        {totalSelected}
                      </span>
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {totalSelected === 0 && (
                <TooltipContent side="bottom">Select items to share</TooltipContent>
              )}
            </Tooltip>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Folder options"
                className="grid h-9 w-9 place-items-center rounded-full bg-secondary text-muted-foreground transition-colors hover:text-foreground"
              >
                <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                Rename
              </DropdownMenuItem>
              {showSettings && (
                <DropdownMenuItem onSelect={() => setSettingsOpen(true)}>
                  <Settings />
                  Folder settings
                </DropdownMenuItem>
              )}
              {showDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={handleDelete}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

        {/* Scrolling content */}
        <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-6">

      {/* Rename + settings dialogs at page-level (three-dot menu triggers them). */}
      <FolderNameDialog
        mode="rename"
        initialName={folder.name}
        open={renameOpen}
        onOpenChange={setRenameOpen}
        onSubmit={handleRename}
      />
      {showSettings && (
        <FolderSettingsDialog
          folder={{
            id: folder.id,
            name: folder.name,
            projectId: folder.projectId,
            parentFolderId: folder.parentFolderId,
            restricted: folder.restricted,
            canEnter: true,
            restrictedAncestorId: null,
          }}
          projectId={projectId!}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />
      )}

      {showInvite && (
        <InviteToFolderDialog
          folderId={folder.id}
          folderName={folder.name}
          restricted={folder.restricted}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      )}

      {showShare && (
        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          items={shareItems}
        />
      )}

      {/* Content */}
      <div className="pt-8">
        {nothingAtAll && (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed border-border py-24 text-center">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-muted-foreground">
              <UploadCloud className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-muted-foreground">
              This folder is empty. Drop files here or create a subfolder.
            </p>
            <Button asChild>
              <Link to={uploadHref}>Upload</Link>
            </Button>
          </div>
        )}

        {subfolders.length > 0 && (
          <div className="mb-8">
            <p className="mb-3 text-sm text-muted-foreground">
              {subfolders.length} {subfolders.length === 1 ? "Folder" : "Folders"}
            </p>
            <div className={gridClass}>
              {subfolders.map((sf) => (
                <FolderCard
                  key={sf.id}
                  folder={sf}
                  view={appearance}
                  foldersBasePath={foldersBasePath}
                  projectId={projectId!}
                  selected={selectedFolders.has(sf.id)}
                  onToggleSelected={() => toggleFolderSelected(sf.id)}
                  onSelectExclusive={() => selectFolderOnly(sf.id)}
                />
              ))}
            </div>
          </div>
        )}

        {videos.length > 0 && (
          <div>
            <p className="mb-3 text-sm text-muted-foreground">
              {videos.length} {videos.length === 1 ? "Asset" : "Assets"} ·{" "}
              {formatBytes(totalBytes)}
            </p>

            {visible.length === 0 && query.trim() ? (
              <p className="py-16 text-center text-muted-foreground">
                No videos match "{query}".
              </p>
            ) : (
              <>
                <div className={gridClass}>
                  {pagedVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      projectId={projectId!}
                      video={video}
                      view={appearance}
                      selected={selectedIds.has(video.id)}
                      onToggleSelected={() => toggleSelected(video.id)}
                      onSelectExclusive={() => selectVideoOnly(video.id)}
                      watchPath={watchPath}
                    />
                  ))}
                </div>
                {hasMore && <div ref={sentinelRef} aria-hidden className="h-1" />}
              </>
            )}
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-muted-foreground">
              Couldn't load this folder's contents.
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        )}
      </div>
        </div>
      </div>

      {/* Selection action bar — same footprint as project-detail. */}
      {totalSelected > 0 && (
        <SelectionActionBar
          selectedVideos={selectedVideoObjects}
          selectedFolders={selectedFolderObjects}
          onCleared={clearSelection}
          onDismiss={clearSelection}
          onTrashVideos={async (ids) => {
            await Promise.all(ids.map((id) => deleteVideo.mutateAsync(id)));
          }}
          onPermanentDeleteVideos={async (ids) => {
            await Promise.all(ids.map((id) => deleteVideoPermanent.mutateAsync(id)));
          }}
          onCompare={(a, b) => {
            const base = watchPath.startsWith("/admin/") ? "/admin/compare" : "/app/compare";
            navigate(`${base}/${a}/${b}`);
          }}
        />
      )}
    </ProjectShell>
  );
}
